import { useMutation } from '@apollo/client';
import { faExternalLink, faCheck, faArrowsLeftRight, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { camelCase } from 'lodash';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { ConnectionIndicator } from './ConnectionIndicator.tsx';
import { useAgentsForViewer } from './useAgentsForViewer.ts';
import OutlookLogo from '../../../../../static/images/outlook.svg';
import AgentLogo from '../../../../../static/images/worki_agent.png';
import WorkistLogo from '../../../../../static/images/workist_icon.svg';
import { useFilterOptions } from '../../../analytics/utils/dataByDocumentType.ts';
import { ForceSelectionAutoCompleteField } from '../../../core/components/Fields/AutoCompleteField.tsx';
import Field from '../../../core/components/Fields/Field.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import ProgressModal from '../../../core/components/ProgressModal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { parseRedirectUrl } from '../../../core/utils/link.ts';
import { DocumentType, DOCUMENT_TYPE } from '../../../document/constants.ts';
import { CONNECT_PERSONAL_SMART_INBOX_TO_AGENT } from '../../../products/assistants/queries.ts';
import { SETUP_PERSONAL_MAILBOX } from '../../queries.ts';

const ExternalLinkIcon = withIcon(faExternalLink);
const CheckIcon = withIcon(faCheck);
const ArrowLeftRightIcon = withIcon(faArrowsLeftRight);
const ClearIcon = withIcon(faXmark);

interface Props {
    isOpen: boolean;
    onClose: () => void;
    connectOutlook: () => Promise<any>;
    channelForm: any;
    hasErrorIntegration: boolean;
    setHasErrorIntegration: (v: boolean) => void;
    searchParams: URLSearchParams;
    setSearchParams: (p: any) => void;
    onSetupSuccess?: () => Promise<void>;
}

const ALLOWED_DOCUMENT_TYPES = [DocumentType.Order, DocumentType.Rfq];

export const PersonalSmartInboxModal: React.FC<Props> = ({
    isOpen,
    onClose,
    connectOutlook,
    channelForm,
    setHasErrorIntegration,
    searchParams,
    onSetupSuccess,
    setSearchParams,
}) => {
    const { t } = useTranslation('users');
    const [modalStep, setModalStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
    const channelsPrefilledRef = useRef(false);

    const { channelsByDocumentType, loading } = useFilterOptions();
    const channelFormErrors = channelForm.formState.errors;
    const { publishToast } = useToaster();

    const { agents: teamAgents, loading: agentsLoading } = useAgentsForViewer(!isOpen);
    const [connectPersonalSmartInboxToAgent] = useMutation(CONNECT_PERSONAL_SMART_INBOX_TO_AGENT);

    useEffect(() => {
        const outlookIntegration = searchParams.get('outlookIntegration');
        if (outlookIntegration === 'success') {
            setModalStep(1);
        } else if (outlookIntegration === 'error') {
            setHasErrorIntegration(true);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!isOpen) {
            channelForm.reset();
            setSelectedAgentId(null);
            channelsPrefilledRef.current = false;
        }
    }, [isOpen, channelForm]);

    useEffect(() => {
        if (teamAgents.length === 1) {
            setSelectedAgentId(teamAgents[0].id);
        }
    }, [teamAgents]);

    useEffect(() => {
        if (loading || !channelsByDocumentType || modalStep !== 1 || channelsPrefilledRef.current) {
            return;
        }

        ALLOWED_DOCUMENT_TYPES.forEach((documentType) => {
            const documentTypeKey = camelCase(DocumentType[documentType]);
            const channels = channelsByDocumentType[documentTypeKey] || [];

            if (channels.length === 1) {
                const fieldName = documentType === DocumentType.Order ? 'orderChannel' : 'rfqChannel';
                if (!channelForm.getValues(fieldName)) {
                    channelForm.setValue(fieldName, channels[0].value);
                }
            }
        });
        channelsPrefilledRef.current = true;
    }, [loading, channelsByDocumentType, modalStep, channelForm]);

    const totalSteps = 4;
    const modalProgress = ((modalStep + 1) / totalSteps) * 100;
    const handleCloseModal = async () => {
        setModalStep(0);
        setSearchParams(new URLSearchParams());
        if (onSetupSuccess) {
            await onSetupSuccess();
        }

        onClose();
    };
    const [setupPersonalMailbox] = useMutation(SETUP_PERSONAL_MAILBOX);
    const handleNextStep = async () => {
        setIsLoading(true);
        if (modalStep === 0) {
            const ALLOWED_ORIGINS = [
                'https://login.microsoftonline.com',
                'https://outlook.office.com',
                window.location.origin,
            ];
            // redirecting to outlook sign in page
            try {
                const { data } = await connectOutlook();
                const redirectUrl = data?.connectPersonalMailboxAuth?.redirectUrl;

                if (redirectUrl) {
                    try {
                        const url = parseRedirectUrl(redirectUrl);
                        if (ALLOWED_ORIGINS.includes(url.origin)) {
                            setIsLoading(false);
                            window.location.href = url.toString();
                        } else {
                            console.error('Blocked unsafe redirect:', redirectUrl);
                        }
                    } catch {
                        console.error('Invalid redirect URL:', redirectUrl);
                    }
                }
            } catch (err) {
                setIsLoading(false);
                console.error(err);
                publishToast({ description: t('personalSmartInbox.errorRedirect'), status: 'error' });
            }
        } else if (modalStep === 1) {
            // channel selection
            const channelData = channelForm.getValues();
            setIsLoading(true);
            try {
                const channels = [
                    { documentType: DOCUMENT_TYPE.ORDER, channelId: channelData.orderChannel },
                    { documentType: DOCUMENT_TYPE.REQUEST_FOR_QUOTATION, channelId: channelData.rfqChannel },
                ].filter((config) => config.channelId);

                await setupPersonalMailbox({
                    variables: {
                        channels,
                    },
                });
                setModalStep(modalStep + 1);
                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setIsLoading(false);
                publishToast({ description: t('personalSmartInbox.setupError'), status: 'error' });
            }
        } else if (modalStep === 2) {
            // agent step
            if (teamAgents.length > 0 && selectedAgentId) {
                try {
                    await connectPersonalSmartInboxToAgent({
                        variables: {
                            agentId: selectedAgentId,
                            connect: true,
                        },
                    });
                    setModalStep(modalStep + 1);
                    setIsLoading(false);
                } catch (error) {
                    console.error(error);
                    setIsLoading(false);
                    publishToast({
                        description: t('personalSmartInbox.agentConnectionError'),
                        status: 'error',
                    });
                }
            } else {
                // no agents available, just advance
                setModalStep(modalStep + 1);
                setIsLoading(false);
            }
        } else if (modalStep === 3) {
            // finish set up
            if (onSetupSuccess) {
                await onSetupSuccess();
            }
            setIsLoading(false);
            handleCloseModal();

            publishToast({ description: t('personalSmartInbox.connectionSuccess'), status: 'success' });
        } else if (modalStep < totalSteps - 1) {
            setModalStep(modalStep + 1);
            setIsLoading(false);
        } else {
            setIsLoading(false);
            handleCloseModal();

            publishToast({ description: t('personalSmartInbox.connectionSuccess'), status: 'success' });
        }
    };

    const agentStepTitle =
        teamAgents.length === 0
            ? t('personalSmartInbox.agentStepTitleNoAgents')
            : t('personalSmartInbox.agentStepTitleReady');

    const agentStepConfirm =
        teamAgents.length === 0 ? t('personalSmartInbox.gotIt') : t('personalSmartInbox.enableAgent');

    const MODAL_TITLES = [
        t('personalSmartInbox.connectToOutlook'),
        t('personalSmartInbox.connectChannels'),
        agentStepTitle,
        t('personalSmartInbox.allSet'),
    ];

    const MODAL_CONFIRMS = [
        t('personalSmartInbox.connectToOutlook'),
        t('personalSmartInbox.finish'),
        agentStepConfirm,
        t('personalSmartInbox.understood'),
    ];

    const getConfirmIcon = () => {
        switch (modalStep) {
            case 0:
                return <ExternalLinkIcon className="pl-2" />;
            default:
                return null;
        }
    };

    const getModalContent = useCallback(() => {
        const features = ['Classification', 'Forwarding', 'Zero', 'Control'];

        switch (modalStep) {
            case 0:
                return (
                    <div className="space-y-4">
                        <div className="flex items-center justify-center gap-4 py-4">
                            <div className="rounded flex items-center justify-center">
                                <img src={OutlookLogo} alt="Outlook" className="w-[40px] h-[40px]" />
                            </div>
                            <div className="text-2xl">
                                <ArrowLeftRightIcon className="text-content-weak text-2xl" />
                            </div>
                            <div className="rounded flex items-center justify-center">
                                <img src={WorkistLogo} className="w-[32px] h-[32px]" />
                            </div>
                        </div>
                        <p className="text-content-strong text-sm font-normal">
                            {t('personalSmartInbox.modalDescription')}
                        </p>
                        {features.map((feature) => (
                            <div key={feature} className="space-y-3">
                                <div>
                                    <h3 className="font-semibold text-content-strong text-sm">
                                        {t(`personalSmartInbox.feature${feature}`)}
                                    </h3>
                                    <p className="text-content-strong text-xs font-normal">
                                        {t(`personalSmartInbox.feature${feature}Description`)}
                                    </p>
                                </div>
                            </div>
                        ))}

                        <p className="text-sm font-normal text-content-strong pt-2">
                            {t('personalSmartInbox.redirectNotice')}
                        </p>
                    </div>
                );
            case 1:
                const getFieldNameForDocumentType = (documentType: DocumentType) => {
                    if (documentType === DocumentType.Order) return 'orderChannel';
                    if (documentType === DocumentType.Rfq) return 'rfqChannel';
                    return '';
                };

                return (
                    <div className="space-y-4">
                        <div className="space-y-3">
                            <div>
                                <p className="text-content-strong text-sm font-normal">
                                    {t('personalSmartInbox.channelSelectionModalHeader')}
                                </p>
                            </div>
                            {loading ? (
                                <div className="text-content-weak text-sm">
                                    {t('personalSmartInbox.channelsLoading')}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {ALLOWED_DOCUMENT_TYPES.map((documentType) => {
                                        const documentTypeKey = camelCase(DocumentType[documentType]);
                                        const channels = channelsByDocumentType[documentTypeKey] || [];
                                        const channelOptions = channels?.map(
                                            (channel: { label: string; value: string }) => ({
                                                label: channel.label,
                                                value: channel.label,
                                                id: channel.value,
                                            })
                                        );
                                        const fieldName = getFieldNameForDocumentType(documentType) as
                                            | 'orderChannel'
                                            | 'rfqChannel';

                                        if (channels.length === 0) return null;

                                        return (
                                            <Controller
                                                key={documentType}
                                                name={fieldName}
                                                control={channelForm.control}
                                                render={({ field }) => {
                                                    const selectedOption = channelOptions.find(
                                                        (opt: { id: string; label: string; value: string }) =>
                                                            opt.id === field.value
                                                    );
                                                    const displayValue = selectedOption?.label || '';

                                                    return (
                                                        <FormField
                                                            label={t(
                                                                `personalSmartInbox.${camelCase(documentTypeKey)}Channel`,
                                                                {
                                                                    defaultValue: documentTypeKey,
                                                                }
                                                            )}
                                                            error={channelFormErrors[fieldName]}
                                                        >
                                                            {channelOptions.length > 0 ? (
                                                                <ForceSelectionAutoCompleteField
                                                                    options={channelOptions}
                                                                    value={displayValue}
                                                                    renderOption={(option) => (
                                                                        <span className="text-content-strong">
                                                                            {option.label}
                                                                        </span>
                                                                    )}
                                                                    onValueChange={(label: string) => {
                                                                        const selected = channelOptions.find(
                                                                            (opt: {
                                                                                id: string;
                                                                                label: string;
                                                                                value: string;
                                                                            }) => opt.label === label
                                                                        );
                                                                        field.onChange(selected?.id || '');
                                                                    }}
                                                                    onBlur={field.onBlur}
                                                                    onFocus={() => {}}
                                                                    placeholder={t('personalSmartInbox.selectChannel')}
                                                                    controls={
                                                                        field.value ? (
                                                                            <Field.ControlButton
                                                                                aria-label={t(
                                                                                    'personalSmartInbox.clearChannel'
                                                                                )}
                                                                                onClick={() => field.onChange('')}
                                                                            >
                                                                                <ClearIcon />
                                                                            </Field.ControlButton>
                                                                        ) : undefined
                                                                    }
                                                                />
                                                            ) : null}
                                                        </FormField>
                                                    );
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 2:
                if (agentsLoading) {
                    return (
                        <div className="space-y-4">
                            <div className="text-content-weak text-sm">{t('personalSmartInbox.agentsLoading')}</div>
                        </div>
                    );
                }

                if (teamAgents.length === 0) {
                    return (
                        <div className="space-y-4">
                            <div className="flex items-center justify-center gap-4 py-4">
                                <div className="rounded flex items-center justify-center">
                                    <img src={OutlookLogo} alt="Outlook" className="w-[40px] h-[40px]" />
                                </div>
                                <ConnectionIndicator isConnected={false} className="w-12" />
                                <div className="rounded flex items-center justify-center">
                                    <img src={AgentLogo} alt="Agent" className="w-[40px] h-[40px]" />
                                </div>
                            </div>
                            <p className="text-content-strong text-sm font-normal">
                                <Trans i18nKey="personalSmartInbox.agentInfoNoAgents" t={t}>
                                    Save even more time with the <strong>Workist Inbox Agent</strong>.
                                    <br />
                                    <br />
                                    The IT admin can now activate the agent for your team. Workist drafts emails for you
                                    directly in your Outlook Add-in by checking your ERP, master data, or connected
                                    SharePoint folders.
                                </Trans>
                            </p>
                        </div>
                    );
                }

                if (teamAgents.length === 1) {
                    return (
                        <div className="space-y-4">
                            <div className="flex items-center justify-center gap-4 py-4">
                                <div className="rounded flex items-center justify-center">
                                    <img src={OutlookLogo} alt="Outlook" className="w-[40px] h-[40px]" />
                                </div>
                                <ConnectionIndicator isConnected className="w-12" />
                                <div className="rounded flex items-center justify-center">
                                    <img src={AgentLogo} alt="Agent" className="w-[40px] h-[40px]" />
                                </div>
                            </div>
                            <p className="text-content-strong text-sm font-normal">
                                <Trans
                                    i18nKey="personalSmartInbox.agentInfoSingle"
                                    t={t}
                                    values={{ agentName: teamAgents[0].name }}
                                >
                                    Your IT Admin has configured the <strong>{'{{agentName}}'}</strong> for your team.
                                    This agent will help draft responses based on your ERP or SharePoint data directly
                                    in Outlook.
                                    <br />
                                    <br />
                                    Once activated, use the Workist Add-in to see how the agent analyzes unstructured
                                    text and prepares a draft for you.
                                </Trans>
                            </p>
                        </div>
                    );
                }

                const agentOptions = teamAgents.map((agent: { id: string; name: string }) => ({
                    label: agent.name,
                    value: agent.id,
                }));

                return (
                    <div className="space-y-4">
                        <div className="flex items-center justify-center gap-4 py-4">
                            <div className="rounded flex items-center justify-center">
                                <img src={OutlookLogo} alt="Outlook" className="w-[40px] h-[40px]" />
                            </div>
                            <ConnectionIndicator isConnected className="w-12" />
                            <div className="rounded flex items-center justify-center">
                                <img src={AgentLogo} alt="Agent" className="w-[40px] h-[40px]" />
                            </div>
                        </div>
                        <p className="text-content-strong text-sm font-normal">
                            {t('personalSmartInbox.agentInfoMultiple')}
                        </p>
                        <FormField label={t('personalSmartInbox.selectAgent')}>
                            <SelectField
                                options={agentOptions}
                                value={selectedAgentId || ''}
                                onValueChange={(value: string) => setSelectedAgentId(value || null)}
                                placeholder={t('personalSmartInbox.selectAgentPlaceholder')}
                            />
                        </FormField>
                        <p className="text-content-strong text-sm font-normal">
                            {t('personalSmartInbox.agentInfoMultipleHint')}
                        </p>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-4">
                        <div className="flex items-center justify-center gap-4 py-4">
                            <div className="rounded flex items-center justify-center">
                                <img src={OutlookLogo} alt="Outlook" className="w-[40px] h-[40px]" />
                            </div>
                            <ConnectionIndicator isConnected className="w-12" />
                            <div className="rounded flex items-center justify-center">
                                <img src={WorkistLogo} className="w-[32px] h-[32px]" />
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="text-content-strong text-sm font-normal">
                                    {t('personalSmartInbox.inboxReady')}
                                </p>
                            </div>
                            <div>
                                <p className="text-content-strong pb-3 text-sm font-normal">
                                    {t('personalSmartInbox.nextStepTitle')}
                                </p>
                                {Array.from({ length: 3 }).map((_, index) => (
                                    <p key={index} className="text-content-strong text-xs pb-2 font-normal">
                                        <CheckIcon className="pr-2" />
                                        {t(`personalSmartInbox.nextStep${index + 1}`)}
                                    </p>
                                ))}

                                {selectedAgentId &&
                                    (() => {
                                        const connectedAgent = teamAgents.find(
                                            (a: { id: string }) => a.id === selectedAgentId
                                        );
                                        if (!connectedAgent) return null;
                                        return (
                                            <p className="text-content-strong text-xs pb-2 font-normal">
                                                <CheckIcon className="pr-2" />
                                                <Trans
                                                    i18nKey="personalSmartInbox.agentConnected"
                                                    t={t}
                                                    values={{ agentName: connectedAgent.name }}
                                                >
                                                    The <strong>{'{{agentName}}'}</strong> agent is enabled and ready to
                                                    assist you.
                                                </Trans>
                                            </p>
                                        );
                                    })()}
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    }, [modalStep, loading, channelsByDocumentType, teamAgents, agentsLoading, selectedAgentId]);

    const isAgentStepConfirmDisabled = modalStep === 2 && teamAgents.length > 1 && !selectedAgentId;

    return (
        <>
            <ProgressModal
                isOpen={isOpen}
                onClose={handleCloseModal}
                hideClose={modalStep === 1}
                title={MODAL_TITLES[modalStep]}
                progress={modalProgress}
                onConfirm={handleNextStep}
                loading={isLoading || (modalStep === 2 && agentsLoading)}
                confirmIcon={getConfirmIcon()}
                confirmText={MODAL_CONFIRMS[modalStep]}
                confirmDisabled={isAgentStepConfirmDisabled}
            >
                {getModalContent()}
            </ProgressModal>
        </>
    );
};
